
import { Component, Provide, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State, Action } from "vuex-class"

import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import InsightParameters from "@/components/Insights/InsightParameters.vue"
import TableResults from "@/components/Insights/TableResults.vue"
import ChartResults from "@/components/Insights/ChartResults.vue"
import HeatMap from "@/components/Insights/HeatMap.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import CalculationTable from "@/components/Insights/CalculationTable.vue"
import RetractableSidebar from "@/components/RetractableSidebar.vue"
import InsightSettings from "@/components/Insights/InsightSettings.vue"
import { inlineEditMode } from "@/types/insights/selectiveInlineEdit"
import { CalculationResults } from "@/types/insights/calculatedResults"
import { TenantFeatures } from "@/types/tenantFeatures"
import * as InsightUtils from "@/scripts/insights"

import { CurrentUser, ExtendedPermissions } from "@/types/user"
import { CaseDetails, DayOfWeek, Team, TemplateDetails, TimeZone } from "@/types"
import {
	Insight,
	LabelFormat,
	Operator,
	InsightCalculation,
	insightViews,
	DynamicParameter,
	InsightSummary,
	InsightDetails,
	createDefaultInsight,
	InsightViewType
} from "@/types/insights"

import ViewFolders from "@/components/Folders/ViewFolders.vue"
import { Clause } from "@/types/view"

import { CaseColumn, caseColumns } from "@/types/caseColumns"

import cloneDeep from "lodash/cloneDeep"

import api from "@/services/api"

// @ts-ignore
import Guid from "@/scripts/guid"
import { csvExport } from "@/scripts/csvDownload"
import applyDefaultTimezoneToClauses, { applyTimezone } from "@/scripts/defaultTimezone.ts"
import { Folder, FolderType } from "@/types/folder"
import { UserColumn } from "@/types/userColumns"
import { duplicateInsight, insightDuplicationOptions, InsightDuplicationOption } from "@/types/insights/insightDuplication"
import _ from "lodash"
import { TenantSettingKeys } from "@/types/tenantSetting"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import { folderMixin } from "@/mixins/folderMixin"
import { DateTime } from "luxon"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"
import InsightDeleteModal from "./InsightDeleteModal.vue"
import { CalculationTableFormattingOptions, InsightFormattingOptions, createDefaultInsightFormattingOptions, createDefaultCalculationTableFormattingOptions } from "@/types/insights/formattingOptions"
import DataCache from "@/caching/caching"
import { InsightPresetKeys } from "@/types/insights/presets/insightPreset"
import * as TimeManagementWeeklyReportInsightPreset from "@/scripts/insights/presets/timeManagementWeeklyReportInsightPreset"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"page": PageWrapper,
		"scrollbar": VuePerfectScrollbar,
		"parameters": InsightParameters,
		"results-table": TableResults,
		"chart-results": ChartResults,
		"heat-map": HeatMap,
		"skeleton-loader": SkeletonLoader,
		"calculation-table": CalculationTable,
		"view-folders": ViewFolders,
		"retractable-sidebar": RetractableSidebar,
		"insight-settings": InsightSettings,
		"v-select": vSelect,
		"help-icon": HelpIcon,
		"insight-delete": InsightDeleteModal
	}
})

export default class Insights extends Vue {
	@State timeZones!: TimeZone[]
	@State displayTimezone!: TimeZone | null
	@State private readonly templateCache!: DataCache<TemplateDetails>

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getCaseName") public caseName!: string;

	@Getter("getScreenWidth") public screenWidth!: number;

	@setup.Getter("getTeams")
	teams!: Team[];

	showSelectColumn: boolean = false;

	@setup.Action
	loadTeams!: () => Promise<void>

	@setup.Action loadDataSources!: () => Promise<void>

	@setup.Getter("getFilterableFields")
	getFilterableFields!: CaseColumn[];

	@setup.Getter("getFilterableUserFields")
	getFilterableUserFields!: UserColumn[];

	@Getter("getTenantSettings")
	getTenantSettings!: { [ key: string]: any }

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getIsFeatureEnabled")
	isFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter getDefaultFirstDayOfWeek!: DayOfWeek

	@setup.Action("loadFields")
	loadFields!: () => Promise<void>;

	@caseStore.Action private detailsLoadIfNeeded!: () => Promise<void>;

	/* --- Constants --- */

	InsightViewType = InsightViewType
	private readonly perPagePerformanceWarningThreshold = 500

	/* --- Data --- */

	labelFormat = LabelFormat.Text;
	insights: InsightSummary[] = [];
	selectedInsight: Insight | null = null;

	executedInsight: Insight | null = null

	queryColumns: any[] = [
		{
			key: "label",
			label: "Field"
		},
		{
			key: "operatorLabel",
			label: "Operator"
		},
		{
			key: "value",
			label: "Value"
		}
	];

	perPage: number | string = 50
	perPageOptions = [ 10, 50, 100, 250, 500, 1000, 2500, "All"	]

	chartType: string | null = null;
	fields: any[] = [];
	userFields: UserColumn[] = [];

	columns: any[] = [];
	calculations: any[] = [];
	calculationTotals: any[] = [];

	results: any = null;
	resultGroups: readonly any[] = [];
	calculationGroupByLabels: any = {};

	deletingInsight: InsightDetails | null = null;
	deletingCases: boolean = false;
	deleteConfirmCase: any = null;

	awaitingInsightDetails: boolean = false;
	runningInsight: boolean = false;
	insightError: boolean = false
	insightTitle: string = "";

	folders: Folder[] = []
	parameters: DynamicParameter[] = []
	editSettings: boolean = false

	insightFilter: string | null = null

	insightToBeDuplicated: InsightDetails | null = null
	selectedOptionsToDuplicate: string[] = []
	duplicatingInsightModalActive: boolean = false
	showHiddenCalculations: boolean = false;
	showCalculationFormattingOptions: boolean = false

	isMounted: boolean = false
	bulkCaseDelete: { [key: string]: string[] } = {}

	editModeEnabled: boolean = false
	private cacheDataLoaded: boolean = false

	private get hasMapPointColumn (): boolean {
		return this.columns.some(c => c && c.dataType === "map-marker")
	}

	private get validCalcGroupBy (): boolean {
		return this.calculations && this.calculations.every(c => c.groupByKeyLabels !== undefined)
	}

	private get hasResults (): boolean {
		return this.resultGroups && this.resultGroups.length > 0 && this.resultGroups.some(group => group.results && group.results.length > 0)
	}

	private get itemsPerPage (): number {
		const perPage = parseInt(this.perPage.toString())
		return isNaN(perPage) ? 0 : perPage
	}

	private get filteredInsights (): InsightSummary[] {
		const lowerFilter = this.insightFilter ? this.insightFilter.toLowerCase() : ""
		return this.insightFilter
			? this.insights.filter(i => i.title.toLowerCase().includes(lowerFilter))
			: this.insights
	}

	private get timezoneFeatureEnabled (): boolean {
		return this.getTenantSettings && this.isFeatureEnabled(TenantFeatures.UiTimezone)
	}

	private get isInsightFormattingOptionsFeatureEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.InsightFormattingOptions)
	}

	private get isInlineEditByRowFeatureEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.InlineEditByRow)
	}

	private get inlineEditIconLabelText (): string {
		return this.editModeEnabled ? "Close inline editing sidebar" : "Open inline editing sidebar"
	}

	public get isInlineEditSetToNone (): boolean {
		return this.selectedInsight?.inlineEditMode === inlineEditMode.None
	}

	private get timezoneName (): string {
		if (!this.executedInsight) return ""

		const insightTimezone = this.executedInsight.timeZone
			? this.executedInsight.timeZone
			: this.displayTimezone
				? this.displayTimezone.iana
				: (new DateTime()).setZone("system").zoneName

		const timezone = this.timeZones.find(tz => tz.iana === insightTimezone)
		return timezone ? timezone.title : insightTimezone
	}

	private get calculationTableFormattingOptions (): CalculationTableFormattingOptions {
		return this.selectedInsight?.formattingOptionsAdHoc?.calculationTable ??
			this.selectedInsight?.formattingOptions?.calculationTable ??
			createDefaultCalculationTableFormattingOptions()
	}

	public async mounted () {
		await this.loadInsightsAndFolders()

		if (this.me && this.me.editInsights) {
			this.loadInsightParameters()
			if (this.teams.length === 0) {
				this.loadTeams()
			}
		}

		// just get fields from Vuex
		await this.loadFields()
		this.fields = this.getFilterableFields
		this.userFields = this.getFilterableUserFields

		if (this.insights.length === 0) {
			this.newInsight()
		}

		this.loadDataSources()

		this.isMounted = true
	}

	private async loadInsightsAndFolders () {
		const insightsPromise = api.insightsGet()
		const foldersPromise = api.foldersGet(FolderType.Insight)

		await Promise.allSettled([insightsPromise, foldersPromise])

		this.insights = await insightsPromise
		this.folders = await foldersPromise

		// If there is a selected Insight, ensure the folder name is up to date
		if (this.selectedInsight) {
			const folderName: string = this.insights.firstOrDefault(f => f.id === this.selectedInsight?.id).folder

			// Folder names do not match, so set from the updated list of Insights
			if (this.selectedInsight.folder !== folderName) {
				this.selectedInsight.folder = folderName
			}
		}
	}

	// Retrieve all insight Parameters
	private async loadInsightParameters () {
		this.parameters = await api.insightParameters()
	}

	private prepareToRunInsight (insight: Insight): Insight {
		insight = cloneDeep(insight)
		this.executedInsight = null
		if (insight.formattingOptionsAdHoc) {
			insight.formattingOptionsAdHoc = null
		}

		applyTimezone(insight)

		this.resultGroups = []
		this.columns = insight.displayColumns
			? insight.displayColumns!.map((c: any) => {
				const columnClone = cloneDeep(c) as any
				return columnClone
			})
			: []
		this.insightTitle = insight.title

		this.runningInsight = true
		this.insightError = false
		this.editModeEnabled = false

		return insight
	}

	/**
	 * Get the default view type for the provided insight.
	 * @param insight The insight to get the default view for.
	 * @return The default view for the insight.
	 */
	private getInsightDefaultView (insight: Insight): string {
		let defaultView = insight.defaultView
		if (!defaultView) {
			defaultView = InsightViewType.Table
		} else {
			// Ensure selected view is still applicable to this insight.
			const view = insightViews.find(v => v.key === defaultView)
			if (!view || !view.canDisplay(insight, this.calculations, this.fields, false)) {
				defaultView = InsightViewType.Table
			}
		}
		return defaultView
	}

	/**
	 * Runs the provided insight, updating the component's results when complete.
	 * This is the only intended entry point for running insights.
	 * @param search The insight to run.
	 */
	public async runInsight (search: Insight) {
		const insight = this.prepareToRunInsight(search)

		try {
			if (insight.presetKey) {
				await this.runPresetInsight(search, insight)
			} else {
				await this.runStandardInsight(search, insight)
			}
		} catch (error) {
			console.error(error)
			this.insightError = true
		} finally {
			this.runningInsight = false
		}
	}

	/**
	 * Runs a standard insight.
	 * This should not be called directly, use `runInsight` instead.
	 * @param search The original insight instance.
	 * @param prepared The prepared insight to run.
	 */
	private async runStandardInsight (search: Insight, prepared: Insight): Promise<void> {
		const insight = prepared

		const cachedDataLoadPromise = this.loadCacheData()

		const groupedResults = await api.insights(insight)
		this.executedInsight = insight

		// The Insight has changed whilst the api data was returning so the data is for the wrong insight
		if (this.selectedInsight?.id !== insight.id) {
			return
		}

		let calculations = groupedResults.calculations ? groupedResults.calculations.map((calc: InsightCalculation) => {
			return {
				...calc,
				groupByKeyLabels: groupedResults.calculationGroupByLabels[calc.key]
					? groupedResults.calculationGroupByLabels[calc.key]
					: {}
			}
		}) : []

		await cachedDataLoadPromise

		this.calculations = calculations
		this.calculationTotals = insight.calculationTotals!

		this.labelFormat = groupedResults.labelFormat
		this.resultGroups = Object.freeze(groupedResults.results)
		this.calculationGroupByLabels = groupedResults.calculationGroupByLabels

		let defaultView = this.getInsightDefaultView(insight)
		this.chartType = defaultView
	}

	/**
	 * Runs an insight that uses a preset.
	 * This should not be called directly, use `runInsight` instead.
	 * @param search The original insight instance.
	 * @param prepared The prepared insight to run.
	 */
	private async runPresetInsight (search: Insight, insight: Insight) {
		const cachedDataLoadPromise = this.loadCacheData()

		const groupedResults = await api.insightWithPreset(insight)

		// The Insight has changed whilst the api data was returning so the data is for the wrong insight
		if (this.selectedInsight?.id !== insight.id) {
			return
		}

		await cachedDataLoadPromise
		this.executedInsight = insight

		this.resultGroups = Object.freeze(groupedResults.results)

		switch (insight.presetKey) {
			case InsightPresetKeys.TimeManagementWeeklyReport:
				TimeManagementWeeklyReportInsightPreset
					.addPresetColumns(this.columns, insight.presetParameters!, this.getDefaultFirstDayOfWeek)
				break
			default:
				throw new Error("Unknown preset key: " + insight.presetKey)
		}

		let defaultView = this.getInsightDefaultView(insight)
		this.chartType = defaultView
	}

	private newInsight () {
		this.selectedInsight = createDefaultInsight()
	}

	private async saveInsightToDbAndRefresh (insight: Insight) {
		insight.id = await api.insightSave(insight)
		this.insights = await api.insightsGet()

		if (insight.folder && insight.folder !== "") {
			if (!this.folders.find(folder => folder.title === insight.folder)) {
				this.loadInsightsAndFolders()
			}
		}
	}

	private async saveInsight (insight: Insight) {
		try {
			await this.saveInsightToDbAndRefresh(insight)

			this.$bvToast.toast("Insight saved", {
				title: "Saved",
				variant: "success",
				solid: true,
				autoHideDelay: 1000
			})
		} catch (err) {
			console.error(err)
			this.$bvToast.toast(err as string, {
				title: "Saving error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		}
	}

	public get showDuplicationModal () : boolean {
		return this.getTenantSettings && this.getTenantSettings[TenantSettingKeys.ShowInsightDuplicationOptionsPopup] === true
	}

	public get doNotDuplicateInsightOptionDefaults () : InsightDuplicationOption[] {
		if (this.getTenantSettings && this.getTenantSettings[TenantSettingKeys.DoNotDuplicateInsightOptionDefaults]) {
			const optionsToDefaultIds : string[] = this.getTenantSettings[TenantSettingKeys.DoNotDuplicateInsightOptionDefaults]
			if (optionsToDefaultIds.length === 0) {
				return []
			}
			return this.insightDuplicationOptions.filter(option => optionsToDefaultIds.includes(option.id))
		}
		return []
	}

	public get selectedOptionsToNotDuplicate (): string[] {
		const selectedOptionsToNotDuplicate = this.insightDuplicationOptions
			.filter(option => !this.selectedOptionsToDuplicate.includes(option.id))
			.map(option => option.id)
		return selectedOptionsToNotDuplicate
	}

	public get insightDuplicationOptions () : InsightDuplicationOption[] {
		return insightDuplicationOptions
	}

	private async duplicateInsight (insight: InsightDetails) {
		this.insightToBeDuplicated = insight
		this.selectedOptionsToDuplicate = _.differenceWith(insightDuplicationOptions, this.doNotDuplicateInsightOptionDefaults, (a: InsightDuplicationOption, b: InsightDuplicationOption) => a.id === b.id)
			.map(c => c.id)

		if (this.showDuplicationModal) {
			this.duplicatingInsightModalActive = true
		} else {
			await this.duplicateInsightConfirmed()
		}
	}

	private async duplicateInsightConfirmed () {
		const insightToBeDuplicated = this.insightToBeDuplicated!
		const duplicatedInsight = duplicateInsight(insightToBeDuplicated, this.selectedOptionsToNotDuplicate)
		try {
			await this.saveInsightToDbAndRefresh(duplicatedInsight)

			this.insightToBeDuplicated = null
			this.selectInsight(duplicatedInsight)

			this.$bvToast.toast(`Insight '${insightToBeDuplicated.title}' duplicated`, {
				title: "Duplicated",
				variant: "success",
				solid: true,
				autoHideDelay: 1000
			})
			this.duplicatingInsightModalActive = false
		} catch (err) {
			console.error(err)
			this.$bvToast.toast(err as string, {
				title: "Duplicate Insight error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		}
	}

	private deleteInsight (insight: InsightDetails) {
		this.deletingInsight = insight
	}

	private async deleteConfirm () {
		if (this.deletingInsight) {
			await api.insightDelete(this.selectedInsight!.id!)

			this.deletingInsight = null
			this.selectedInsight = null
			this.insights = await api.insightsGet()
		}

		// Bulk Delete Cases
		if (this.deletingCases) {
			await api.bulkDeleteCase(this.deleteConfirmCase)

			this.deleteConfirmCase = null
			this.deletingCases = false
			this.showSelectColumn = false
			this.bulkCaseDelete = {}

			if (this.selectedInsight !== null) {
				await this.runInsight(this.selectedInsight)
			}
		}
	}

	private deleteCase () {
		this.deleteConfirmCase = this.bulkDeleteItems
		this.deletingCases = true
	}

	private async selectInsight (insight: Insight): Promise<void> {
		this.editSettings = false
		this.showSelectColumn = false
		this.showHiddenCalculations = false

		if (!this.selectedInsight || (insight.id !== this.selectedInsight.id && !this.awaitingInsightDetails)) {
			let insightDetails: InsightDetails | null = null
			this.awaitingInsightDetails = true

			try {
				insightDetails = await api.insightGet(insight.id!) as InsightDetails
			} catch (e) {
				console.log(e)
			} finally {
				this.awaitingInsightDetails = false
			}

			this.resultGroups = []

			// Create keys for any calculations missing a key
			if (insightDetails) {
				if (insightDetails.calculations) {
					insightDetails.calculations.forEach((calc: any) => {
						if (!calc.key) {
							calc.key = Guid.newGuid()
						}

						if (calc.expression === `{${calc.label}}` || calc.expression === "{value}") {
							// Clear expressions that do nothing
							calc.expression = null
						} else if (calc.expression) {
							calc.expression = calc.expression.replace(`{${calc.label}}`, "{value}")
						}
					})
				}
				if (insightDetails.calculationTotals) {
					insightDetails.calculationTotals.forEach((calc: any) => {
						if (!calc.key) {
							calc.key = Guid.newGuid()
						}

						if (calc.expression === `{${calc.title}}` || calc.expression === "{value}") {
							// Clear expressions that do nothing
							calc.expression = null
						} else if (calc.expression) {
							calc.expression = calc.expression.replace(`{${calc.title}}`, "{value}")
						}
					})
				}

				if (insightDetails.clauses) {
					insightDetails.clauses.forEach((clause: any) => this.setClauseFieldData(clause, insightDetails!))
				}

				if (insightDetails.clauses.some(clause => clause.field === "hiddenFromInsights")) {
					// The insight has already been setup by an admin to have 'hiddenFromInsights' so they need to have the fields available.
					const hiddenFromInsightsColumn = caseColumns.find(cc => cc.key === "hiddenFromInsights")
					if (!this.fields.includes(hiddenFromInsightsColumn)) {
						this.fields.push(hiddenFromInsightsColumn)
					}
				}

				this.selectedInsight = cloneDeep(insightDetails)
				this.editModeEnabled = false
				if (this.selectedInsight!.defaultView) {
					this.chartType = this.selectedInsight!.defaultView
				}

				if (this.chartType === "HeatMap") {
					if (!this.hasMapPointColumn) {
						this.chartType = null
					}
				}
			}
		}
	}

	private setClauseFieldData (clause: Clause, insight: InsightDetails) {
		let fieldData: CaseColumn | null = null
		if (clause.field?.startsWith("calculation.")) {
			const calculationColumn = insight.calculationColumns.find(c => c.key === clause.field)
			const calculation = insight.calculations.find(c => c.key === clause.field?.substring("calculation.".length))
			if (calculation) {
				fieldData = InsightUtils.getCalculationAsCaseColumn(clause.field, calculation.label)
			} else if (calculationColumn) {
				fieldData = InsightUtils.getCalculationColumnAsCaseColumn(clause.field, calculationColumn.title)
			}
		} else if (clause.field?.startsWith("calculationTotal.")) {
			const calculationTotal = insight.calculationTotals?.find(c => c.key === clause.field?.substring("calculationTotal.".length))
			if (calculationTotal) {
				fieldData = InsightUtils.getCalculationTotalAsCaseColumn(clause.field, calculationTotal.title)
			}
		} else {
			fieldData = this.fields.find(f => f.key === clause.field)
		}

		if (fieldData !== null) {
			Vue.set(clause, "fieldData", fieldData)
		}
		if (clause.subClauses) {
			clause.subClauses.forEach(subClause => this.setClauseFieldData(subClause, insight))
		}
	}

	private getFieldValue (field: any, record: any, key: string, obj: any) {
		return obj
	}

	private getFieldLabel (fieldId: string, caseDetails: CaseDetails): string | null {
		if (!caseDetails.fields[fieldId]) {
			return null
		}

		return caseDetails.fields[fieldId].label
	}

	// Converts the results into a downloaded-able CSV
	private downloadResults () {
		const exportColumns = cloneDeep(this.columns)
		const exportResultGroups = cloneDeep(this.resultGroups)

		csvExport.addAdditionalColumnsForDownload(exportColumns, exportResultGroups, this.getIsFeatureEnabled, this.caseName)

		const result = csvExport.resultsToCsv(exportColumns, exportResultGroups)

		const urlObject = window.URL || window
		const url = urlObject.createObjectURL(new Blob([result]))
		const el = document.createElement("a")
		const title = this.insightTitle ? this.insightTitle + ".csv" : "download.csv"
		el.setAttribute("href", url)
		el.setAttribute("download", title)
		el.click()
		urlObject.revokeObjectURL(url)
	}

	// Maps the user role field type values into a string ready for the papa-parse csv converter
	private mapUsersFromRole (record: any, users: any, hasSubRoles: boolean): string {
		try {
			if (!hasSubRoles) {
				return users.map(
					// @ts-ignore
					user => {
						let userRoleString = ""
						let usersName = record.users!.find((u: { id: any; }) => u.id === user.id)
						if (usersName) {
							userRoleString = usersName.name
						}
						if (user.timeSpent) {
							userRoleString += " (" + user.timeSpent + ")"
						}
						return userRoleString
					}
				).join(", ")
			} else {
				return users.map(
					// @ts-ignore
					subRole => {
						return subRole.title + ": " + this.mapUsersFromRole(record, subRole.users, false)
					}
				).join(". ")
			}
		} catch {
		}

		return ""
	}

	// If a value of a field is an object type, return a string value of that object
	private objectToString (object: any) {
		try {
			if (typeof (object) === "object") {
				return JSON.stringify(object)
			} else {
				return object
			}
		} catch (err) {
			return object
		}
	}

	private configureSettings () {
		this.editSettings = true
	}

	/**
	 * Clear insight folders that match deleted
	 */
	private folderDeleted (folder: Folder) {
		let folderPath: string
		// The folder may be a sub folder, so get the full path
		if (folder.parentFolderId) {
			folderPath = folderMixin.methods.getPathToRootFolder(folder, this.folders, true)
		} else {
			folderPath = folder.title
		}

		const folderInsights = this.insights.filter(insight => insight.folder === folderPath)
		folderInsights.forEach(insight => insight.folder = "")

		// Also remove the folder for the selected Insight (if one is selected)
		if (this.selectedInsight && folderInsights.firstOrDefault(f => f.id === this.selectedInsight?.id)) {
			this.selectedInsight.folder = ""
		}
	}

	private get bulkDeleteCasesEnabled () {
		if (this.me.tenantEnabledFeatures!.includes(TenantFeatures.BulkDeletePermissions)) {
			if (this.me.extendedPermissions && this.me.extendedPermissions[ExtendedPermissions.BulkDelete]) {
				return !this.showSelectColumn
			}
		} else if (this.me.tenantEnabledFeatures!.includes(TenantFeatures.BulkDeleteCases) && this.me.isAdmin) {
			return !this.showSelectColumn
		}

		return false
	}

	private get deleteCasesText () {
		return this.bulkDeleteItemCount > 1 ? this.caseName + "s" : this.caseName
	}

	private get bulkDeleteItemCount (): number {
		return this.bulkDeleteItems.length
	}

	private get bulkDeleteItems (): string [] {
		return Object.keys(this.bulkCaseDelete).map(k => this.bulkCaseDelete[k]).flat()
	}

	private setBulkDelete (item: {groupValue: string, items:string []}) {
		Vue.set(this.bulkCaseDelete, item.groupValue, item.items)
	}

	private clearBulkDelete () {
		this.showSelectColumn = false
		if (this.bulkCaseDelete) {
			this.bulkCaseDelete = {}
		}
	}

	private get showCalculationsButtonEnabled ():boolean {
		return this.getIsFeatureEnabled(TenantFeatures.HideCalculations) &&
		!!(this.calculations?.find(c => c.hidden) || this.calculationTotals?.find(c => c.hidden))
	}

	private get resultsCount (): number {
		return this.resultGroups.reduce((acc, group) => acc + (group.results?.length ?? 0), 0)
	}

	private createAdHocFormattingOptionsIfNeeded () {
		if (!this.selectedInsight) return
		if (!this.selectedInsight.formattingOptionsAdHoc) {
			Vue.set(this.selectedInsight, "formattingOptionsAdHoc", createDefaultInsightFormattingOptions())
		}
	}

	private calculationTableAdHocFormattingChanged (formattingOptions: CalculationTableFormattingOptions) {
		if (!this.selectedInsight) return
		this.createAdHocFormattingOptionsIfNeeded()
		Vue.set(this.selectedInsight.formattingOptionsAdHoc!, "calculationTable", formattingOptions)
	}

	/**
	 * Ensures that data used by CaseTable is loaded
	 */
	private async loadCacheData (): Promise<void> {
		if (this.cacheDataLoaded) return

		const detailsLoadPromise = this.detailsLoadIfNeeded()
		const fetchTemplatesDataCachePromise = this.templateCache.fetchIfNeeded()

		await Promise.allSettled([detailsLoadPromise, fetchTemplatesDataCachePromise])
		this.cacheDataLoaded = true
	}

	@Watch("chartType")
	private onChartTypeChange (newChartType: string | null) {
		if (newChartType !== "table") {
			this.editModeEnabled = false
		}
	}

	private clearCalculations (): void {
		InsightUtils.clearCalculations(this.selectedInsight!)
	}

	private clearGroupBy (): void {
		InsightUtils.clearGroupBy(this.selectedInsight!)
	}

	private clearPreset (): void {
		InsightUtils.clearPreset(this.selectedInsight!)
	}
}

